import React, { useState } from 'react';
import { ChevronLeft, ChevronDown, CheckCircle } from 'lucide-react';
import Header from '../components/Header';
import ContactForm from '../components/ContactForm';
import MOTTestItem from '../components/MOTTestItem';
import { formatDate, formatDateShort } from '../utils/dateUtils';

const ResultsPage = ({ results, searchQuery, onBack }) => {
  console.log('ResultsPage received results:', results);
  const [showAllSections, setShowAllSections] = useState(false);
  const [showHistory, setShowHistory] = useState(true);
  const [showContactForm, setShowContactForm] = useState(false);

  // Detect if this is RDW (Dutch) data
// More accurate detection - RDW data has 'found' and 'basic' properties
  const isRDW = results.hasOwnProperty('found') && results.hasOwnProperty('basic');  
  // Handle Dutch vehicles not found
  if (isRDW && results.found === false) {
    return (
      <div className="min-h-screen bg-white">
        <Header onContactClick={() => setShowContactForm(true)} />
        <main className="max-w-5xl mx-auto px-4 py-8">
          <button
            onClick={onBack}
            className="flex items-center text-blue-600 underline hover:text-blue-800 mb-6"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Back
          </button>
          <div className="text-center py-12">
            <h2 className="text-2xl font-bold mb-4">Vehicle Not Found</h2>
            <p className="text-gray-600 mb-4">{results.message}</p>
            <button
              onClick={onBack}
              className="text-blue-600 underline hover:text-blue-800"
            >
              Try another registration
            </button>
          </div>
        </main>
        {showContactForm && (
          <ContactForm onClose={() => setShowContactForm(false)} />
        )}
      </div>
    );
  }


// Handle Dutch vehicles found
if (isRDW && results.found === true) {
  return (
    <div className="min-h-screen bg-white">
      <Header onContactClick={() => setShowContactForm(true)} />
      <main className="max-w-5xl mx-auto px-4 py-8">
        <button
          onClick={onBack}
          className="flex items-center text-blue-600 underline hover:text-blue-800 mb-6"
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          Back
        </button>
        
        <div className="mb-8">
          <div className="flex items-start gap-4 mb-6">
            <div className="bg-yellow-400 px-4 py-2 font-bold text-2xl border-2 border-black">
              {results.registration}
            </div>
            <h1 className="text-5xl font-bold">
              {results.basic?.make} {results.basic?.model}
            </h1>
          </div>
          
          <button
            onClick={onBack}
            className="text-blue-600 underline hover:text-blue-800 mb-6"
          >
            Check another vehicle
          </button>

          {/* Key Status Indicators */}
          <div className="grid grid-cols-2 gap-4 mb-6 p-4 bg-gray-50 rounded">
            <div className={`font-bold ${results.basic?.insured ? 'text-green-600' : 'text-red-600'}`}>
              Insurance: {results.basic?.insured ? '✓ Insured' : '✗ Not Insured'}
            </div>
            <div className={`font-bold ${new Date(results.basic?.apk_expiry_date) > new Date() ? 'text-green-600' : 'text-red-600'}`}>
              APK: {new Date(results.basic?.apk_expiry_date) > new Date() ? '✓ Valid until ' : '✗ Expired on '}
              {new Date(results.basic?.apk_expiry_date).toLocaleDateString('en-GB')}
            </div>
            {results.basic?.recall_active && (
              <div className="text-orange-600 font-bold col-span-2">
                ⚠ Outstanding Recall Active
              </div>
            )}
          </div>

          {/* Vehicle Details Grid */}
          <div className="grid grid-cols-3 gap-6 mb-8">
            <div>
              <div className="text-gray-600 mb-1">Colour</div>
              <div className="text-xl font-bold">
                {results.basic?.color || 'Not recorded'}
              </div>
            </div>
            <div>
              <div className="text-gray-600 mb-1">Fuel type</div>
              <div className="text-xl font-bold">
                {results.fuel?.euro_standard ? 
                  `${results.basic?.fuel_type || 'Unknown'} (${results.fuel.euro_standard})` : 
                  results.basic?.fuel_type || 'Not recorded'}
              </div>
            </div>
            <div>
              <div className="text-gray-600 mb-1">Date registered</div>
              <div className="text-xl font-bold">
                {results.basic?.first_registration ? 
                  new Date(results.basic.first_registration.substring(0,4), 
                           results.basic.first_registration.substring(4,6)-1, 
                           results.basic.first_registration.substring(6,8)).toLocaleDateString('en-GB', {
                    month: 'short',
                    year: 'numeric'
                  }) : 'Not recorded'}
              </div>
            </div>
          </div>

          {/* Vehicle Specifications */}
          <div className="border-t-2 border-gray-300 pt-6">
            <h2 className="text-2xl font-bold mb-4">Vehicle Specifications</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <span className="text-gray-600">Type: </span>
                <span className="font-bold">{results.basic?.type}</span>
              </div>
              <div>
                <span className="text-gray-600">Body type: </span>
                <span className="font-bold">{results.basic?.body_type}</span>
              </div>
              <div>
                <span className="text-gray-600">Engine: </span>
                <span className="font-bold">{results.basic?.engine_capacity}cc ({results.basic?.cylinders} cylinders)</span>
              </div>
              <div>
                <span className="text-gray-600">CO2 emissions: </span>
                <span className="font-bold">{results.fuel?.co2_emissions} g/km</span>
              </div>
              <div>
                <span className="text-gray-600">Fuel efficiency: </span>
                <span className="font-bold">Class {results.basic?.fuel_efficiency_class}</span>
              </div>
              <div>
                <span className="text-gray-600">Fuel consumption: </span>
                <span className="font-bold">{results.fuel?.combined_consumption} L/100km</span>
              </div>
              <div>
                <span className="text-gray-600">Weight: </span>
                <span className="font-bold">{results.basic?.weight} kg (max: {results.basic?.max_weight} kg)</span>
              </div>
              <div>
                <span className="text-gray-600">Seats/Doors: </span>
                <span className="font-bold">{results.basic?.seats} seats, {results.basic?.doors} doors</span>
              </div>
              <div>
                <span className="text-gray-600">Towing capacity: </span>
                <span className="font-bold">{results.basic?.towing_braked} kg (braked)</span>
              </div>
              <div>
                <span className="text-gray-600">Catalog price: </span>
                <span className="font-bold">€{parseInt(results.basic?.catalog_price).toLocaleString()}</span>
              </div>
              <div>
                <span className="text-gray-600">BPM tax: </span>
                <span className="font-bold">€{parseInt(results.basic?.bpm_tax).toLocaleString()}</span>
              </div>
              <div>
                <span className="text-gray-600">Odometer status: </span>
                <span className="font-bold">{results.basic?.odometer_judgment} (last: {results.basic?.last_odometer_year})</span>
              </div>
            </div>
          </div>

          {/* APK History if available */}
          {results.apk_history && results.apk_history.length > 0 && (
            <div className="border-t-2 border-gray-300 pt-6 mt-6">
              <h2 className="text-2xl font-bold mb-4">APK Defect Reports</h2>
              <div className="space-y-2">
                {results.apk_history.map((apk, index) => (
                  <div key={index} className="p-3 bg-gray-50 rounded">
                    <div className="font-bold">
                      {new Date(apk.datetime).toLocaleDateString('en-GB')} - 
                      {apk.defects_found} defect(s) found
                    </div>
                    <div className="text-sm text-gray-600">
                      Type: {apk.inspection_type}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
      {showContactForm && (
        <ContactForm onClose={() => setShowContactForm(false)} />
      )}
    </div>
  );
}

  return (
    <div className="min-h-screen bg-white">
      <Header onContactClick={() => setShowContactForm(true)} />

      <main className="max-w-5xl mx-auto px-4 py-8">
        {/* Back link */}
        <button 
          onClick={onBack}
          className="flex items-center text-blue-600 underline hover:text-blue-800 mb-6"
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          Back
        </button>

        {/* Vehicle Header */}
        <div className="mb-8">
          <div className="flex items-start gap-4 mb-6">
            <div className="bg-yellow-400 px-4 py-2 font-bold text-2xl border-2 border-black">
              {results.registration || searchQuery}
            </div>
            <h1 className="text-5xl font-bold">
              {results.make} {results.model}
            </h1>
          </div>

          <button 
            onClick={onBack}
            className="text-blue-600 underline hover:text-blue-800 mb-6"
          >
            Check another vehicle
          </button>

          {/* Vehicle Details Grid */}
          <div className="grid grid-cols-3 gap-6 mb-8">
            <div>
              <div className="text-gray-600 mb-1">Colour</div>
              <div className="text-xl font-bold">
                {results.primaryColour || results.colour || 'Not recorded'}
              </div>
            </div>
            <div>
              <div className="text-gray-600 mb-1">Fuel type</div>
              <div className="text-xl font-bold">{results.fuelType || 'Not recorded'}</div>
            </div>
            <div>
              <div className="text-gray-600 mb-1">Date registered</div>
              <div className="text-xl font-bold">
                {formatDateShort(results.registrationDate || results.firstUsedDate)}
              </div>
            </div>
          </div>

          {/* MOT Status Banner */}
          {results.motTests && results.motTests.length > 0 && (
            <div className="mb-6">
              <div className="text-gray-600 mb-2">MOT valid until</div>
              <div className="text-4xl font-bold mb-4">
                {formatDate(results.motTests[0].expiryDate)}
              </div>
            </div>
          )}

          {/* Correction Link */}
          <div className="mb-8">
            <span className="text-gray-700">You can </span>
            <a href="#" className="text-blue-600 underline hover:text-blue-800">
              get information corrected on your MOT history
            </a>
            <span className="text-gray-700"> (such as mileage or vehicle details) if it's wrong.</span>
          </div>

          {/* Show/Hide All Sections */}
          <button 
            onClick={() => setShowAllSections(!showAllSections)}
            className="flex items-center text-blue-600 underline hover:text-blue-800 mb-6"
          >
            <ChevronDown className={`h-4 w-4 mr-1 transition-transform ${showAllSections ? 'rotate-180' : ''}`} />
            {showAllSections ? 'Hide all sections' : 'Show all sections'}
          </button>
        </div>

        {/* MOT History Section */}
        <div className="border-t-2 border-gray-300 pt-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-2xl font-bold">MOT history</h2>
              <p className="text-gray-600">Check mileage recorded at test, expiry date, and test outcome</p>
            </div>
          </div>

          <button 
            onClick={() => setShowHistory(!showHistory)}
            className="flex items-center text-blue-600 underline hover:text-blue-800 mb-4"
          >
            <ChevronDown className={`h-4 w-4 mr-1 transition-transform ${!showHistory ? '-rotate-90' : ''}`} />
            {showHistory ? 'Hide' : 'Show'}
          </button>

          {showHistory && results.motTests && (
            <div>
              {results.motTests.map((test, index) => (
                <MOTTestItem 
                  key={index} 
                  test={test} 
                  formatDate={formatDateShort}
                  index={index}
                />
              ))}
            </div>
          )}
        </div>

        {/* Outstanding Recalls Section */}
        {showAllSections && results.hasOutstandingRecall && (
          <div className="border-t-2 border-gray-300 pt-6 mt-8">
            <h2 className="text-2xl font-bold mb-4">Outstanding vehicle recalls</h2>
            <p className="text-gray-600">
              Check if this vehicle has any outstanding manufacturer's safety recalls.
            </p>
            <div className="mt-4 p-4 bg-yellow-50 border-l-4 border-yellow-400">
              <p className="font-bold">Outstanding recall found</p>
              <p className="text-gray-700 mt-2">
                This vehicle has been recalled by the manufacturer. Contact your dealer for more information.
              </p>
            </div>
          </div>
        )}

        {/* Additional Vehicle Information */}
        {showAllSections && (
          <div className="border-t-2 border-gray-300 pt-6 mt-8">
            <h2 className="text-2xl font-bold mb-4">Additional vehicle information</h2>
            <div className="grid grid-cols-2 gap-4">
              {results.engineSize && (
                <div>
                  <span className="text-gray-600">Engine size: </span>
                  <span className="font-bold">{results.engineSize}cc</span>
                </div>
              )}
              {results.manufactureDate && (
                <div>
                  <span className="text-gray-600">Date of manufacture: </span>
                  <span className="font-bold">{formatDateShort(results.manufactureDate)}</span>
                </div>
              )}
              {results.co2Emissions && (
                <div>
                  <span className="text-gray-600">CO2 emissions: </span>
                  <span className="font-bold">{results.co2Emissions} g/km</span>
                </div>
              )}
              {results.euroStatus && (
                <div>
                  <span className="text-gray-600">Euro status: </span>
                  <span className="font-bold">{results.euroStatus}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Footer Links */}
        <div className="border-t-2 border-gray-300 pt-6 mt-12">
          <h3 className="text-lg font-bold mb-4">Related services</h3>
          <ul className="space-y-2">
            <li>
              <a href="#" className="text-blue-600 underline hover:text-blue-800">
                Getting an MOT
              </a>
            </li>
            <li>
              <a href="#" className="text-blue-600 underline hover:text-blue-800">
                Check vehicle tax status
              </a>
            </li>
            <li>
              <a href="#" className="text-blue-600 underline hover:text-blue-800">
                Check if a vehicle is insured
              </a>
            </li>
          </ul>
        </div>

        <div className="border-t border-gray-300 pt-6 mt-8">
          <p className="text-sm text-gray-600">
            This is an independent service providing MOT history data. 
            We are not affiliated with the DVSA or any government agency.
          </p>
        </div>
      </main>

      <ContactForm 
        isOpen={showContactForm} 
        onClose={() => setShowContactForm(false)} 
      />
    </div>
  );
};

export default ResultsPage;
